import { toKebabCase, toPascalCase } from '@core-engine/utils/string.js'
import { Node, Project, type SourceFile, type ts } from 'ts-morph'

export interface FieldRenameSelection {
  before: string
  after: string
}

export interface CustomCellRenameSelection {
  before: string
  after: string
  oldPath: string
  newPath: string
  columnsPath: string
}

export interface RenamePlan {
  fields: FieldRenameSelection[]
  customCells: CustomCellRenameSelection[]
}

export interface RenamePreviewChange {
  path: string
  line: number
  before: string
  after: string
}

export interface RenameTransformResult {
  changed: boolean
  content: string
  parseFailed: boolean
  previewChanges: RenamePreviewChange[]
}

interface TextEdit {
  start: number
  end: number
  replacement: string
}

const project = new Project({
  useInMemoryFileSystem: true,
  skipAddingFilesFromTsConfig: true
})

function escapeForRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function quoteLiteral(value: string, quote: string): string {
  const escaped = value
    .replace(/\\/g, '\\\\')
    .replace(new RegExp(escapeForRegex(quote), 'g'), `\\${quote}`)

  return `${quote}${escaped}${quote}`
}

function applyTextEdits(content: string, edits: TextEdit[]): string {
  return edits
    .sort((a, b) => b.start - a.start)
    .reduce(
      (next, edit) => next.slice(0, edit.start) + edit.replacement + next.slice(edit.end),
      content
    )
}

function collectPreviewChanges(
  path: string,
  beforeContent: string,
  afterContent: string
): RenamePreviewChange[] {
  const beforeLines = beforeContent.split('\n')
  const afterLines = afterContent.split('\n')
  const maxLength = Math.max(beforeLines.length, afterLines.length)
  const changes: RenamePreviewChange[] = []

  for (let index = 0; index < maxLength; index++) {
    const before = beforeLines[index] ?? ''
    const after = afterLines[index] ?? ''

    if (before === after) {
      continue
    }

    changes.push({
      path,
      line: index + 1,
      before: before.trim(),
      after: after.trim()
    })
  }

  return changes
}

function hasParseDiagnostics(sourceFile: SourceFile): boolean {
  const diagnostics =
    (sourceFile.compilerNode as ts.SourceFile & { parseDiagnostics?: readonly ts.Diagnostic[] })
      .parseDiagnostics ?? []
  return diagnostics.length > 0
}

function getJsxAttributeName(node: Node): string | null {
  let current: Node | undefined = node

  while (current) {
    const parent = current.getParent()
    if (!parent) {
      return null
    }

    if (Node.isJsxAttribute(parent)) {
      return parent.getNameNode().getText()
    }

    if (Node.isSourceFile(parent)) {
      return null
    }

    current = parent
  }

  return null
}

function getPropertyNameForInitializer(node: Node): string | null {
  const parent = node.getParent()
  if (!parent) {
    return null
  }

  if (Node.isPropertyAssignment(parent) && parent.getInitializer() === node) {
    return parent.getName()
  }

  return null
}

function getTemplateReplacement(node: Node, literalText: string): string {
  if (Node.isTemplateHead(node)) {
    return `\`${literalText}\${`
  }

  if (Node.isTemplateMiddle(node)) {
    return `}${literalText}\${`
  }

  return `}${literalText}\``
}

function replacePathSegments(value: string, before: string, after: string): string {
  const variants: Array<[string, string]> = [
    [`${before}Html`, `${after}Html`],
    [before, after]
  ]

  let next = value
  for (const [from, to] of variants) {
    next = next.replace(
      new RegExp(`(^|[.\\-/])${escapeForRegex(from)}(?=$|[.\\-/])`, 'g'),
      (_, prefix: string) => `${prefix}${to}`
    )
  }

  return next
}

function replaceIdentifierExact(text: string, before: string, after: string): string {
  const beforePascal = toPascalCase(before)
  const afterPascal = toPascalCase(after)
  const variants = new Map<string, string>([
    [before, after],
    [`${before}Html`, `${after}Html`],
    [`${before}Field`, `${after}Field`],
    [`${before}FieldArray`, `${after}FieldArray`],
    [`${before}Open`, `${after}Open`],
    [`${before}Expanded`, `${after}Expanded`],
    [`${before}From`, `${after}From`],
    [`${before}To`, `${after}To`],
    [`${before}Media`, `${after}Media`],
    [`${before}MediaIds`, `${after}MediaIds`],
    [beforePascal, afterPascal],
    [`${beforePascal}Field`, `${afterPascal}Field`],
    [`${beforePascal}Column`, `${afterPascal}Column`],
    [`${beforePascal}Props`, `${afterPascal}Props`],
    [`set${beforePascal}Open`, `set${afterPascal}Open`],
    [`set${beforePascal}Expanded`, `set${afterPascal}Expanded`]
  ])

  return variants.get(text) ?? text
}

function replaceCustomCellIdentifier(text: string, before: string, after: string): string {
  if (text === before) {
    return after
  }

  if (text === `${before}Props`) {
    return `${after}Props`
  }

  return text
}

function replaceStringLiteralValue(
  value: string,
  filePath: string,
  node: Node,
  plan: RenamePlan
): string {
  let next = value

  for (const rename of plan.fields) {
    const jsxAttribute = getJsxAttributeName(node)
    const propertyName = getPropertyNameForInitializer(node)
    const isFieldPathLiteral =
      jsxAttribute === 'name' ||
      jsxAttribute === 'id' ||
      jsxAttribute === 'htmlFor' ||
      propertyName === 'accessorKey' ||
      propertyName === 'id' ||
      propertyName === 'name'

    if (isFieldPathLiteral) {
      next = replacePathSegments(next, rename.before, rename.after)
      continue
    }

    if (next === rename.before) {
      next = rename.after
      continue
    }

    if (next === `${rename.before}Html`) {
      next = `${rename.after}Html`
    }
  }

  for (const rename of plan.customCells) {
    if (filePath !== rename.columnsPath) {
      continue
    }

    if (next === `./cells/${rename.before}`) {
      next = `./cells/${rename.after}`
    }
  }

  return next
}

function replaceTemplateLiteralValue(value: string, node: Node, plan: RenamePlan): string {
  const jsxAttribute = getJsxAttributeName(node)
  if (jsxAttribute !== 'name' && jsxAttribute !== 'id' && jsxAttribute !== 'htmlFor') {
    return value
  }

  return plan.fields.reduce(
    (next, rename) => replacePathSegments(next, rename.before, rename.after),
    value
  )
}

function replaceIdentifierValue(value: string, filePath: string, plan: RenamePlan): string {
  let next = value

  for (const rename of plan.customCells) {
    if (
      filePath === rename.columnsPath ||
      filePath === rename.oldPath ||
      filePath === rename.newPath
    ) {
      next = replaceCustomCellIdentifier(next, rename.before, rename.after)
    }
  }

  for (const rename of plan.fields) {
    next = replaceIdentifierExact(next, rename.before, rename.after)
  }

  return next
}

function buildEditsForSourceFile(
  sourceFile: SourceFile,
  filePath: string,
  plan: RenamePlan
): TextEdit[] {
  const edits: TextEdit[] = []

  sourceFile.forEachDescendant((node) => {
    if (Node.isIdentifier(node)) {
      const next = replaceIdentifierValue(node.getText(), filePath, plan)
      if (next !== node.getText()) {
        edits.push({
          start: node.getStart(),
          end: node.getEnd(),
          replacement: next
        })
      }
      return
    }

    if (Node.isStringLiteral(node)) {
      const quote = node.getText().startsWith("'") ? "'" : '"'
      const next = replaceStringLiteralValue(node.getLiteralValue(), filePath, node, plan)
      if (next !== node.getLiteralValue()) {
        edits.push({
          start: node.getStart(),
          end: node.getEnd(),
          replacement: quoteLiteral(next, quote)
        })
      }
      return
    }

    if (Node.isNoSubstitutionTemplateLiteral(node)) {
      const next = replaceStringLiteralValue(node.getLiteralText(), filePath, node, plan)
      if (next !== node.getLiteralText()) {
        edits.push({
          start: node.getStart(),
          end: node.getEnd(),
          replacement: `\`${next}\``
        })
      }
      return
    }

    if (Node.isTemplateHead(node) || Node.isTemplateMiddle(node) || Node.isTemplateTail(node)) {
      const next = replaceTemplateLiteralValue(node.getLiteralText(), node, plan)
      if (next !== node.getLiteralText()) {
        edits.push({
          start: node.getStart(),
          end: node.getEnd(),
          replacement: getTemplateReplacement(node, next)
        })
      }
    }
  })

  return edits
}

export function transformContentForRenamePlan(
  content: string,
  filePath: string,
  plan: RenamePlan,
  displayPath = filePath
): RenameTransformResult {
  if (plan.fields.length === 0 && plan.customCells.length === 0) {
    return {
      changed: false,
      content,
      parseFailed: false,
      previewChanges: []
    }
  }

  const sourceFile = project.createSourceFile(
    `rename-${displayPath.replace(/[^\w.-]+/g, '_')}`,
    content,
    { overwrite: true }
  )

  if (hasParseDiagnostics(sourceFile)) {
    return {
      changed: false,
      content,
      parseFailed: true,
      previewChanges: []
    }
  }

  const edits = buildEditsForSourceFile(sourceFile, filePath, plan)
  if (edits.length === 0) {
    return {
      changed: false,
      content,
      parseFailed: false,
      previewChanges: []
    }
  }

  const nextContent = applyTextEdits(content, edits)

  return {
    changed: nextContent !== content,
    content: nextContent,
    parseFailed: false,
    previewChanges: collectPreviewChanges(displayPath, content, nextContent)
  }
}

export function buildCustomCellRenameSelection(
  schemaName: string,
  pagesDir: string,
  before: string,
  after: string
): CustomCellRenameSelection {
  return {
    before,
    after,
    oldPath: `${pagesDir}/${schemaName}/cells/${before}.tsx`,
    newPath: `${pagesDir}/${schemaName}/cells/${after}.tsx`,
    columnsPath: `${pagesDir}/${schemaName}/columns.tsx`
  }
}

export function buildRenamePreviewPaths(
  remotePaths: string[],
  plan: RenamePlan
): Array<{ sourcePath: string; displayPath: string }> {
  const paths = new Map<string, { sourcePath: string; displayPath: string }>()

  for (const remotePath of remotePaths) {
    paths.set(remotePath, {
      sourcePath: remotePath,
      displayPath: remotePath
    })
  }

  for (const rename of plan.customCells) {
    paths.set(rename.oldPath, {
      sourcePath: rename.oldPath,
      displayPath: rename.newPath
    })
    paths.set(rename.columnsPath, {
      sourcePath: rename.columnsPath,
      displayPath: rename.columnsPath
    })
  }

  return Array.from(paths.values()).sort((a, b) => a.displayPath.localeCompare(b.displayPath))
}

export function formatRenameSelectionSummary(plan: RenamePlan): string[] {
  const lines: string[] = []

  if (plan.fields.length > 0) {
    lines.push(
      `field renames: ${plan.fields.map((rename) => `${rename.before} -> ${rename.after}`).join(', ')}`
    )
  }

  if (plan.customCells.length > 0) {
    lines.push(
      `custom cell renames: ${plan.customCells.map((rename) => `${rename.before} -> ${rename.after}`).join(', ')}`
    )
  }

  return lines
}

export function buildRenamePlanAliasMap(plan: RenamePlan): Map<string, string> {
  return new Map(plan.customCells.map((rename) => [rename.newPath, rename.oldPath]))
}

export function isCustomCellPath(filePath: string, rename: CustomCellRenameSelection): boolean {
  return filePath === rename.oldPath || filePath === rename.newPath
}

export function getCustomCellComponentNameCandidates(
  before: string,
  after: string
): Array<[string, string]> {
  return [
    [before, after],
    [`${before}Props`, `${after}Props`],
    [toKebabCase(before), toKebabCase(after)]
  ]
}
